/**
 * MarkdownContent
 *
 * Lightweight markdown renderer for step extended content.
 * Handles common markdown patterns without external dependencies.
 */

import { Box } from '@mantine/core'
import classes from './MarkdownContent.module.css'

interface MarkdownContentProps {
  content: string
}

/**
 * Convert simple markdown to HTML
 * Supports: headers, bold, lists, code, links
 */
function markdownToHtml(markdown: string): string {
  let html = markdown

  // Escape HTML entities first (security)
  html = html
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')

  // Headers (# through ####)
  html = html.replace(/^#### (.+)$/gm, '<h5>$1</h5>')
  html = html.replace(/^### (.+)$/gm, '<h4>$1</h4>')
  html = html.replace(/^## (.+)$/gm, '<h3>$1</h3>')
  html = html.replace(/^# (.+)$/gm, '<h2>$1</h2>')

  // Bold (**text**)
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')

  // Italic (*text* but not **)
  html = html.replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, '<em>$1</em>')

  // Inline code (`code`)
  html = html.replace(/`([^`]+)`/g, '<code>$1</code>')

  // Unordered lists (- item)
  // First, wrap consecutive list items in <ul>
  html = html.replace(/((?:^- .+$\n?)+)/gm, (match) => {
    const items = match
      .split('\n')
      .filter((line) => line.startsWith('- '))
      .map((line) => `<li>${line.slice(2)}</li>`)
      .join('')
    return `<ul>${items}</ul>`
  })

  // Numbered lists (1. item)
  html = html.replace(/((?:^\d+\. .+$\n?)+)/gm, (match) => {
    const items = match
      .split('\n')
      .filter((line) => /^\d+\. /.test(line))
      .map((line) => `<li>${line.replace(/^\d+\. /, '')}</li>`)
      .join('')
    return `<ol>${items}</ol>`
  })

  // Links [text](url)
  html = html.replace(
    /\[([^\]]+)\]\(([^)]+)\)/g,
    '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>'
  )

  // Paragraphs - convert double newlines to paragraph breaks
  // But don't wrap headers or lists
  html = html
    .split(/\n\n+/)
    .map((block) => {
      block = block.trim()
      if (!block) return ''
      // Don't wrap if it's already a block element
      if (
        block.startsWith('<h') ||
        block.startsWith('<ul') ||
        block.startsWith('<ol') ||
        block.startsWith('<p')
      ) {
        return block
      }
      // Convert single newlines to <br> within paragraphs
      return `<p>${block.replace(/\n/g, '<br />')}</p>`
    })
    .join('')

  return html
}

export function MarkdownContent({ content }: MarkdownContentProps) {
  const html = markdownToHtml(content)

  return (
    <Box
      className={classes.markdown}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  )
}
